import Phaser from 'phaser';

export class Lipid extends Phaser.GameObjects.Container {
    private head: Phaser.GameObjects.Arc;
    private tail: Phaser.GameObjects.Rectangle;
    private isPositioned: boolean = false;
    
    constructor(scene: Phaser.Scene, x: number, y: number, color: number) {
        super(scene, x, y);
        
        // Create head (hydrophilic part)
        this.head = scene.add.circle(0, -12, 8, color);
        
        // Create tail (hydrophobic part)
        this.tail = scene.add.rectangle(0, 0, 4, 24, color);
        this.tail.setOrigin(0.5, 0);
        
        // Add components to container
        this.add([this.tail, this.head]);
        
        // Set size for collision detection
        this.setSize(30, 50);
        
        // Add to scene FIRST before making interactive
        scene.add.existing(this);
        
        // Use the most basic setInteractive() call without custom hitArea
        this.setInteractive();
        
        // Set as draggable
        scene.input.setDraggable(this);
        
        // Set data attributes
        this.setData('type', 'lipid');
        this.setData('tooltip', 'Lipid: A phospholipid molecule that forms cell membranes. Drag to assemble the protocell membrane.');
        
        // Set depth to ensure it's above the background but below UI
        this.setDepth(10);
        
        // Log creation
        console.log(`Created lipid at ${x},${y}, interactive: ${!!this.input?.enabled}`);
    }
    
    public setPositioned(positioned: boolean): void {
        this.isPositioned = positioned;
        this.setData('positioned', positioned);
        
        // If no longer positioned, clear the slot data
        if (!positioned) {
            this.setData('slotIndex', undefined);
        }
    }
    
    public isCorrectlyPositioned(): boolean {
        return this.getData('positioned') === true;
    }
    
    public snapToMembrane(centerX: number, centerY: number, angle: number, radius: number): void {
        // Calculate position on membrane
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        
        // Set position
        this.setPosition(x, y);
        
        // Rotate to point outward (perpendicular to the membrane)
        // For a lipid, we want the "tail" pointing inward
        this.setRotation(angle + Math.PI/2);
        
        // Mark as positioned
        this.setPositioned(true);
        
        // Add a visual feedback effect
        this.scene.tweens.add({
            targets: this,
            scale: { from: 1.3, to: 1.0 },
            duration: 300,
            ease: 'Bounce.Out'
        });
        
        console.log(`Lipid snapped to membrane at angle ${angle.toFixed(2)}, position (${x.toFixed(0)}, ${y.toFixed(0)})`);
    }
    
    // Apply gentle floating animation when in water
    public applyFloatingEffect(): void {
        if (this.isPositioned) return;
        
        const scene = this.scene;
        const startX = this.x;
        const startY = this.y;
        const amplitude = 5 + Math.random() * 3; // Random floating amplitude
        const period = 3000 + Math.random() * 2000; // Random period
        const phase = Math.random() * Math.PI * 2; // Random starting phase
        
        scene.tweens.add({
            targets: this,
            x: {
                getEnd: () => startX + Math.sin(phase + scene.time.now / period * Math.PI * 2) * amplitude,
                getStart: () => this.x
            },
            y: {
                getEnd: () => startY + Math.cos(phase + 1 + scene.time.now / period * Math.PI * 2) * amplitude,
                getStart: () => this.y
            },
            duration: 100,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }
} 